'use client';

import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/**
 * ZenModalShell — reusable chrome for auth + settings modals and card
 * pages (e.g. /auth/challenge, /auth/passkey-recovery) so the decoration
 * (top accent line, orange/blue blobs, centered badge + title) lives in
 * ONE place and automatically inherits dark-mode tokens.
 *
 * This component is pure presentation — it does not own the Dialog
 * primitive. Callers pass it as children of a `<DialogContent>` for
 * modals, or place it standalone inside a `<main>` for page-level cards.
 *
 * Before this shell existed the same 40-line block was copy-pasted into
 * AuthModal, InviteMembersModal and AuthChallengePage, each drifting
 * out of sync on dark-mode variants, blob colours, spacing, close-button
 * placement and border-radius — exactly the regression pattern this
 * component exists to prevent.
 */

export interface ZenModalShellProps {
  /**
   * Centered badge above the title. Use <ZenPillBadge> for the
   * ZenOpus-Konto / brand pill style, or <ZenSquareBadge> for the
   * rounded-square icon holder (TwoFactorChallengeForm / passkey
   * recovery / challenge page style). Omit to skip the badge row.
   */
  badge?: React.ReactNode;
  /**
   * Optional content rendered centered between the badge row and the
   * title — used by the auth landing to slot a small ZenOpus logo
   * below the "ZenOpus-Konto" pill without dropping it into the h1.
   */
  headerExtra?: React.ReactNode;
  /** h1 text. Centered, text-xl, tracking-tight. */
  title: React.ReactNode;
  /** Optional descriptive paragraph below the title. Centered, zen-text-muted. */
  subtitle?: React.ReactNode;
  /** If provided, a close button is rendered top-right. */
  onClose?: () => void;
  /** Disable the close button (while a flow is in progress). */
  closeDisabled?: boolean;
  /** Accessible label for the close button. Defaults to "Close". */
  closeLabel?: string;
  /** Body content rendered below the header. */
  children?: React.ReactNode;
  /** Extra classes applied to the outer decorated `<div>`. */
  className?: string;
  /**
   * When false, the top accent line + the two corner blobs are
   * suppressed. Used by surfaces that sit on a page background (e.g.
   * the standalone /auth landing) where those decorations would
   * float as a "modal edge" without an enclosing card. Defaults to
   * true so all existing modal callers stay visually unchanged.
   */
  decorated?: boolean;
}

export function ZenModalShell({
  badge,
  headerExtra,
  title,
  subtitle,
  onClose,
  closeDisabled,
  closeLabel = 'Close',
  children,
  className,
  decorated = true,
}: ZenModalShellProps) {
  return (
    <div className={cn('relative overflow-hidden bg-zen-surface px-5 pb-5 pt-5', className)}>
      {decorated && (
        <>
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zen-border to-transparent" />
          <div
            className="pointer-events-none absolute -top-10 left-0 h-24 w-24 rounded-full bg-orange-200/20 blur-3xl dark:bg-orange-500/10"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute right-0 top-4 h-20 w-20 rounded-full bg-blue-200/10 blur-3xl dark:bg-blue-500/10"
            aria-hidden
          />
        </>
      )}

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          disabled={closeDisabled}
          className="absolute right-4 top-4 z-10 inline-flex size-8 items-center justify-center rounded-full text-zen-text-subtle transition-colors hover:bg-zen-muted hover:text-zen-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zen-ring disabled:cursor-not-allowed disabled:opacity-50"
          aria-label={closeLabel}
        >
          <X className="size-4" />
        </button>
      )}

      {badge && <div className="relative mb-4 flex justify-center">{badge}</div>}
      {headerExtra && <div className="relative mb-4 flex justify-center">{headerExtra}</div>}

      <div className="relative mb-4 text-center">
        <h1 className="text-xl font-semibold tracking-tight text-zen-text">{title}</h1>
        {subtitle && <p className="mx-auto mt-1 max-w-[320px] text-xs leading-5 text-zen-text-muted">{subtitle}</p>}
      </div>

      <div className="relative">{children}</div>
    </div>
  );
}

/**
 * ZenPillBadge — the "ZenOpus-Konto" style pill badge used by the auth
 * and invite modals. Small pill with a status dot (or icon) + label,
 * emerald tint in dark mode.
 */
export interface ZenPillBadgeProps {
  children: React.ReactNode;
  /** Optional leading slot. Defaults to a 1.5×1.5 emerald dot. */
  leading?: React.ReactNode;
  /** When false, no leading slot is rendered. */
  showLeading?: boolean;
  className?: string;
}

export function ZenPillBadge({ children, leading, showLeading = true, className }: ZenPillBadgeProps) {
  const leadingNode = leading ?? <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-zen-border bg-zen-surface px-2.5 py-1 zen-meta font-medium tracking-[0.02em] text-zen-text-muted shadow-sm',
        'dark:border-emerald-500/40 dark:bg-emerald-500/15 dark:text-emerald-300 dark:shadow-[0_0_12px_-2px_rgba(16,185,129,0.35)]',
        className,
      )}
    >
      {showLeading && leadingNode}
      {children}
    </span>
  );
}

/**
 * ZenSquareBadge — the rounded-square icon holder used by the 2FA
 * challenge, passkey-recovery, and sign-in-interrupted views. Accepts a
 * single child icon.
 */
export interface ZenSquareBadgeProps {
  children: React.ReactNode;
  className?: string;
}

export function ZenSquareBadge({ children, className }: ZenSquareBadgeProps) {
  return (
    <span
      className={cn(
        // shrink-0 keeps the badge perfectly square inside flex parents — without
        // it, neighbouring text squashes the badge into an oval.
        'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-zen-muted text-zen-text',
        className,
      )}
      aria-hidden
    >
      {children}
    </span>
  );
}
